import { useState, useRef, useEffect, useMemo } from 'react'
import { Calendar } from 'lucide-react'
import '../../styles/date-range-picker.css'

// ── Season helpers ───────────────────────────────────────────────────

interface Season { label: string; start: string; end: string }

function getSeasons(): { current: Season; preceding: Season } {
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth() // 0-indexed

    let current: Season
    let preceding: Season

    if (m >= 2 && m <= 7) {
        // Mar–Aug → current = SS<yy>
        const yy = String(y).slice(2)
        current = { label: `SS${yy}`, start: `${y}-03-01`, end: `${y}-08-31` }
        // preceding = FW of previous cycle
        const prevFwYear = y - 1
        const prevFwYY = String(prevFwYear).slice(2).padStart(2, '0')
        // FW label uses the year it starts in
        // FW25 = Sep 1, 2025 → Feb 28, 2026
        const fwEndYear = prevFwYear + 1
        const fwEndDay = isLeapYear(fwEndYear) ? 29 : 28
        preceding = { label: `FW${prevFwYY}`, start: `${prevFwYear}-09-01`, end: `${fwEndYear}-02-${fwEndDay}` }
    } else {
        // Sep–Feb → current = FW
        // If Sep–Dec → FW uses current year
        // If Jan–Feb → FW uses previous year
        let fwYear: number
        if (m >= 8) {
            fwYear = y
        } else {
            fwYear = y - 1
        }
        const fwYY = String(fwYear).slice(2).padStart(2, '0')
        const fwEndYear = fwYear + 1
        const fwEndDay = isLeapYear(fwEndYear) ? 29 : 28
        current = { label: `FW${fwYY}`, start: `${fwYear}-09-01`, end: `${fwEndYear}-02-${fwEndDay}` }
        // preceding = SS of same year as FW start
        const ssYY = String(fwYear).slice(2).padStart(2, '0')
        preceding = { label: `SS${ssYY}`, start: `${fwYear}-03-01`, end: `${fwYear}-08-31` }
    }

    return { current, preceding }
}

function isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

// ── Calendar helpers ─────────────────────────────────────────────────

function daysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate()
}

function toDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function todayStr(): string {
    return toDateStr(new Date())
}

// ── Preset definitions ──────────────────────────────────────────────

export interface Preset {
    id: string
    label: string
    getRange: () => { start: string; end: string }
}

export function buildPresets(): Preset[] {
    const now = new Date()
    const { current, preceding } = getSeasons()

    return [
        {
            id: 'last30',
            label: 'Last 30 Days',
            getRange: () => {
                const end = new Date(now)
                const start = new Date(now)
                start.setDate(start.getDate() - 29)
                return { start: toDateStr(start), end: toDateStr(end) }
            }
        },
        {
            id: 'last90',
            label: 'Last 90 Days',
            getRange: () => {
                const end = new Date(now)
                const start = new Date(now)
                start.setDate(start.getDate() - 89)
                return { start: toDateStr(start), end: toDateStr(end) }
            }
        },
        {
            id: 'currentSeason',
            label: current.label,
            getRange: () => ({ start: current.start, end: current.end })
        },
        {
            id: 'precedingSeason',
            label: preceding.label,
            getRange: () => ({ start: preceding.start, end: preceding.end })
        },
        {
            id: 'last365',
            label: 'Last 365 Days',
            getRange: () => {
                const end = new Date(now)
                const start = new Date(now)
                start.setDate(start.getDate() - 364)
                return { start: toDateStr(start), end: toDateStr(end) }
            }
        },
        {
            id: 'lastCalYear',
            label: `Last Calendar Year`,
            getRange: () => {
                const y = now.getFullYear() - 1
                return { start: `${y}-01-01`, end: `${y}-12-31` }
            }
        },
    ]
}

// ── Calendar Month component ────────────────────────────────────────

function CalendarMonth({
    year, month, rangeStart, rangeEnd, onClick, onNavPrev, onNavNext, showNavPrev, showNavNext
}: {
    year: number; month: number
    rangeStart: string; rangeEnd: string
    onClick: (dateStr: string) => void
    onNavPrev?: () => void; onNavNext?: () => void
    showNavPrev?: boolean; showNavNext?: boolean
}) {
    const dow = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
    const firstDay = new Date(year, month, 1).getDay()
    const days = daysInMonth(year, month)
    const today = todayStr()

    const cells: { day: number; dateStr: string; outside: boolean }[] = []
    // prev month padding
    const prevDays = daysInMonth(year, month - 1)
    for (let i = firstDay - 1; i >= 0; i--) {
        const d = new Date(year, month - 1, prevDays - i)
        cells.push({ day: prevDays - i, dateStr: toDateStr(d), outside: true })
    }
    // this month
    for (let d = 1; d <= days; d++) {
        const dt = new Date(year, month, d)
        cells.push({ day: d, dateStr: toDateStr(dt), outside: false })
    }
    // next month padding
    const remaining = 42 - cells.length
    for (let d = 1; d <= remaining; d++) {
        const dt = new Date(year, month + 1, d)
        cells.push({ day: d, dateStr: toDateStr(dt), outside: true })
    }

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']

    return (
        <div className="drp-cal">
            <div className="drp-cal-header">
                {showNavPrev ? <button className="drp-cal-nav" onClick={onNavPrev}>◀</button> : <span style={{ width: 24 }} />}
                <span className="drp-cal-title">{monthNames[month]} {year}</span>
                {showNavNext ? <button className="drp-cal-nav" onClick={onNavNext}>▶</button> : <span style={{ width: 24 }} />}
            </div>
            <div className="drp-cal-grid">
                {dow.map(d => <span key={d} className="drp-cal-dow">{d}</span>)}
                {cells.map((cell, i) => {
                    const isStart = cell.dateStr === rangeStart
                    const isEnd = cell.dateStr === rangeEnd
                    const inRange = rangeStart && rangeEnd && cell.dateStr >= rangeStart && cell.dateStr <= rangeEnd
                    const isToday = cell.dateStr === today
                    const cls = [
                        'drp-cal-day',
                        cell.outside ? 'outside' : '',
                        isStart ? 'range-start' : '',
                        isEnd ? 'range-end' : '',
                        !isStart && !isEnd && inRange ? 'in-range' : '',
                        isToday ? 'today' : '',
                    ].filter(Boolean).join(' ')

                    return (
                        <button key={i} className={cls} onClick={() => onClick(cell.dateStr)}>
                            {cell.day}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

// ── Main DateRangePicker Component ──────────────────────────────────

interface DateRangePickerProps {
    startDate: string
    endDate: string
    activePresetId?: string | null
    onApply: (start: string, end: string, presetId: string | null) => void
    iconOnly?: boolean
}

export default function DateRangePicker({ startDate, endDate, activePresetId, onApply, iconOnly }: DateRangePickerProps) {
    const [open, setOpen] = useState(false)
    const [draftStart, setDraftStart] = useState(startDate)
    const [draftEnd, setDraftEnd] = useState(endDate)
    const [draftPreset, setDraftPreset] = useState<string | null>(activePresetId ?? null)
    const [selectingEnd, setSelectingEnd] = useState(false) // true = next click sets end date
    const containerRef = useRef<HTMLDivElement>(null)

    const presets = useMemo(() => buildPresets(), [])

    // Calendar view month — show the month of the end date
    const endD = new Date(draftEnd + 'T12:00:00')
    const [viewYear, setViewYear] = useState(endD.getFullYear())
    const [viewMonth, setViewMonth] = useState(endD.getMonth())

    // Sync when opening
    useEffect(() => {
        if (open) {
            setDraftStart(startDate)
            setDraftEnd(endDate)
            setDraftPreset(activePresetId ?? null)
            setSelectingEnd(false)
            const d = new Date(endDate + 'T12:00:00')
            // Show so that the end date's month is the right calendar
            const m = d.getMonth()
            const y = d.getFullYear()
            // Left calendar = month before right
            if (m === 0) {
                setViewYear(y - 1)
                setViewMonth(11)
            } else {
                setViewYear(y)
                setViewMonth(m - 1)
            }
        }
    }, [open, startDate, endDate, activePresetId])

    const handlePreset = (p: Preset) => {
        const { start, end } = p.getRange()
        setDraftStart(start)
        setDraftEnd(end)
        setDraftPreset(p.id)
        setSelectingEnd(false)
        // Adjust calendar view
        const d = new Date(end + 'T12:00:00')
        const m = d.getMonth()
        const y = d.getFullYear()
        if (m === 0) { setViewYear(y - 1); setViewMonth(11) }
        else { setViewYear(y); setViewMonth(m - 1) }
    }

    const handleCalClick = (dateStr: string) => {
        setDraftPreset(null) // custom selection
        if (!selectingEnd) {
            setDraftStart(dateStr)
            setDraftEnd(dateStr)
            setSelectingEnd(true)
        } else {
            if (dateStr < draftStart) {
                setDraftEnd(draftStart)
                setDraftStart(dateStr)
            } else {
                setDraftEnd(dateStr)
            }
            setSelectingEnd(false)
        }
    }

    const handleApply = () => {
        onApply(draftStart, draftEnd, draftPreset)
        setOpen(false)
    }

    const navPrev = () => {
        if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11) }
        else setViewMonth(m => m - 1)
    }
    const navNext = () => {
        if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0) }
        else setViewMonth(m => m + 1)
    }

    // Right calendar month
    const rightMonth = viewMonth === 11 ? 0 : viewMonth + 1
    const rightYear = viewMonth === 11 ? viewYear + 1 : viewYear

    // Format display label
    const formatLabel = () => {
        if (activePresetId) {
            const p = presets.find(p => p.id === activePresetId)
            if (p) return `${p.label}  (${startDate} → ${endDate})`
        }
        return `${startDate} → ${endDate}`
    }

    return (
        <div ref={containerRef} style={{ position: 'relative', display: iconOnly ? 'flex' : 'inline-block', width: iconOnly ? '100%' : 'auto', height: iconOnly ? '100%' : 'auto' }}>
            {iconOnly ? (
                <button className={`mobile-footer-btn ${open ? 'active' : ''}`} onClick={() => setOpen(o => !o)} style={{ width: '100%', height: '100%', border: 'none', background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <Calendar size={24} />
                </button>
            ) : (
                <button className="drp-trigger" onClick={() => setOpen(o => !o)}>
                    📆 {formatLabel()}
                </button>
            )}

            {open && (
                <>
                    <div className="drp-overlay" onClick={() => setOpen(false)} />
                    <div className="drp-panel">
                        {/* Presets sidebar */}
                        <div className="drp-presets">
                            <div className="drp-presets-title">← Presets</div>
                            {presets.map(p => (
                                <button
                                    key={p.id}
                                    className={`drp-preset-btn ${draftPreset === p.id ? 'active' : ''}`}
                                    onClick={() => handlePreset(p)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        {/* Main area */}
                        <div className="drp-main">
                            {/* Date inputs */}
                            <div className="drp-inputs">
                                <input
                                    type="date"
                                    className="drp-date-input"
                                    value={draftStart}
                                    onChange={e => { setDraftStart(e.target.value); setDraftPreset(null) }}
                                />
                                <span>→</span>
                                <input
                                    type="date"
                                    className="drp-date-input"
                                    value={draftEnd}
                                    onChange={e => { setDraftEnd(e.target.value); setDraftPreset(null) }}
                                />
                            </div>

                            {/* Dual calendars */}
                            <div className="drp-calendars">
                                <CalendarMonth
                                    year={viewYear} month={viewMonth}
                                    rangeStart={draftStart} rangeEnd={draftEnd}
                                    onClick={handleCalClick}
                                    showNavPrev onNavPrev={navPrev}
                                />
                                <CalendarMonth
                                    year={rightYear} month={rightMonth}
                                    rangeStart={draftStart} rangeEnd={draftEnd}
                                    onClick={handleCalClick}
                                    showNavNext onNavNext={navNext}
                                />
                            </div>

                            {/* Footer */}
                            <div className="drp-footer">
                                <button className="drp-btn" onClick={() => setOpen(false)}>Cancel</button>
                                <button className="drp-btn drp-btn-primary" onClick={handleApply}>Apply</button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
